import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  IconButton,
  alpha,
} from '@mui/material';
import {
  People,
  CheckCircle,
  Cancel,
  AccessTime,
  EventBusy,
  Work,
  Schedule,
  TrendingUp,
  Visibility,
  HolidayVillage,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const ReportStats = ({
  showStats,
  reportType,
  reportData,
  isAdmin,
  onClose,
  totalEmployees,
}) => {
  if (!showStats || !reportData) return null;

  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getMonthlyLeaveDays = (summary) => summary.reduce((sum, emp) => {
    const leaveDays = (emp.leaves || []).reduce((leaveSum, leave) => (
      leaveSum + (Array.isArray(leave.dates) ? leave.dates.length : 0)
    ), 0);
    return sum + leaveDays;
  }, 0);

  const getDailyLeaveCount = (dayData) => {
    const leaveEmployeeIds = new Set();

    (dayData?.leaveEmployees || []).forEach(leave => {
      if (leave.employee_id) leaveEmployeeIds.add(leave.employee_id);
    });

    (dayData?.records || []).forEach(record => {
      if (record.status === 'on_leave') {
        leaveEmployeeIds.add(record.employee_id || record.employee?.id || record.id);
      }
    });

    return Math.max(dayData?.onLeaveCount || 0, leaveEmployeeIds.size);
  };

  // Helper to format minutes
  const formatMinutes = (minutes) => {
    if (!minutes && minutes !== 0) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getStats = () => {
    if (reportType === 'daily' && reportData) {
      // Multi-day format: extract today's data from the most recent day
      if (Array.isArray(reportData.dailyData) && reportData.dailyData.length > 0) {
        // Find today's data - sort by date descending and get the first non-future day
        const sortedDays = [...reportData.dailyData].sort((a, b) => b.date.localeCompare(a.date));
        const today = getTodayDateString();
        const todayData = sortedDays.find(d => d.date === today) || sortedDays[0];

        return {
          totalEmployees: totalEmployees || 0,
          presentToday: todayData?.presentCount || 0,
          absentToday: todayData?.absentCount || 0,
          lateToday: todayData?.lateCount || 0,
          leaveToday: getDailyLeaveCount(todayData),
          totalWorkingHours: formatMinutes(todayData?.totalWorkingMinutes || 0),
          totalBreakTime: formatMinutes(todayData?.totalBreakMinutes || 0),
        };
      }

      // Single-day format
      return {
        totalEmployees: totalEmployees || (reportData.employees?.length || 0),
        presentToday: reportData.totals?.present || 0,
        absentToday: reportData.totals?.absent || 0,
        lateToday: reportData.totals?.late || 0,
        leaveToday: reportData.totals?.onLeave || reportData.leaves?.length || 0,
        totalWorkingHours: reportData.totals?.totalWorkingHours || '0h 0m',
        totalBreakTime: reportData.totals?.totalBreakTime || '0h 0m',
      };
    } else if (reportType === 'weekly' && reportData) {
      return {
        totalEmployees: totalEmployees || 0,
        presentThisWeek: reportData.weekTotals?.totalPresent || 0,
        absentThisWeek: reportData.weekTotals?.totalAbsent || 0,
        lateThisWeek: reportData.weekTotals?.totalLate || 0,
        leaveThisWeek: reportData.weekTotals?.totalOnLeave || reportData.dailyData?.reduce((sum, day) => sum + (day.onLeaveCount || 0), 0) || 0,
        totalWorkingHours: reportData.weekTotals?.totalWorkingHours || '0h 0m',
        totalBreakTime: reportData.weekTotals?.totalBreakTime || '0h 0m',
      };
    } else if (reportType === 'monthly' && reportData) {
      const summary = reportData.summary || [];
      const calculatedPresent = summary.reduce((sum, emp) => sum + (emp.daysPresent || 0), 0);
      const calculatedLate = summary.reduce((sum, emp) => sum + (emp.daysLate || 0), 0);
      const calculatedAbsent = summary.reduce((sum, emp) => sum + (emp.daysAbsent || 0), 0);
      const calculatedLeave = getMonthlyLeaveDays(summary);
      const totalEmps = totalEmployees || (reportData.summary?.length || 0) || (summary.length > 0 ? summary.length : 0);
      return {
        totalEmployees: totalEmps,
        presentThisMonth: calculatedPresent > 0 ? calculatedPresent : (reportData.monthTotals?.totalPresent || 0),
        absentThisMonth: calculatedAbsent > 0 ? calculatedAbsent : (reportData.monthTotals?.totalAbsent || 0),
        lateThisMonth: calculatedLate > 0 ? calculatedLate : (reportData.monthTotals?.totalLate || 0),
        leaveThisMonth: calculatedLeave || reportData.monthTotals?.totalOnLeave || 0,
        totalWorkingHours: reportData.monthTotals?.totalWorkingHours || '0h 0m',
        totalBreakTime: reportData.monthTotals?.totalBreakTime || '0h 0m',
        holidaysCount: reportData.holidaysCount || 0,
      };
    }
    return {};
  };

  const stats = getStats();

  const statCards = [
    { label: 'Total Employees', value: stats.totalEmployees, icon: <People sx={{ fontSize: 32 }} />, color: '#64B5F6' },
    { label: reportType === 'daily' ? 'Present Today' : reportType === 'weekly' ? 'Present This Week' : 'Present This Month', value: stats.presentToday || stats.presentThisWeek || stats.presentThisMonth || 0, icon: <CheckCircle sx={{ fontSize: 32 }} />, color: '#66BB6A' },
    { label: reportType === 'daily' ? 'Absent Today' : reportType === 'weekly' ? 'Absent This Week' : 'Absent This Month', value: stats.absentToday || stats.absentThisWeek || stats.absentThisMonth || 0, icon: <Cancel sx={{ fontSize: 32 }} />, color: '#EF5350' },
    { label: reportType === 'daily' ? 'Late Today' : reportType === 'weekly' ? 'Late This Week' : 'Late This Month', value: stats.lateToday || stats.lateThisWeek || stats.lateThisMonth || 0, icon: <AccessTime sx={{ fontSize: 32 }} />, color: '#FFB74D' },
    { label: reportType === 'daily' ? 'Leave Today' : reportType === 'weekly' ? 'Leave This Week' : 'Leave This Month', value: stats.leaveToday || stats.leaveThisWeek || stats.leaveThisMonth || 0, icon: <EventBusy sx={{ fontSize: 32 }} />, color: '#42A5F5' },
    { label: 'Total Working Hours', value: stats.totalWorkingHours, icon: <Work sx={{ fontSize: 32 }} />, color: '#D4AF37' },
    { label: 'Total Break Time', value: stats.totalBreakTime, icon: <Schedule sx={{ fontSize: 32 }} />, color: '#FF9800' },
  ];

  if (reportType === 'monthly' && stats.holidaysCount !== undefined) {
    statCards.push({
      label: 'Holidays',
      value: stats.holidaysCount,
      icon: <HolidayVillage sx={{ fontSize: 32 }} />,
      color: '#CE93D8',
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <Card sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ 
          p: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, rgba(212,175,55,0.08) 0%, transparent 100%)',
          borderBottom: '1px solid rgba(212,175,55,0.1)',
        }}>
          <Typography variant="h6" sx={{ color: '#D4AF37', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp />
            Quick Stats Overview
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: '#8AA87D' }}>
            <Visibility sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            {statCards.map((stat, idx) => (
              <Grid item xs={6} sm={4} md={2.4} key={idx}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.05, type: 'spring', stiffness: 150 }}
                >
                  <Box sx={{
                    textAlign: 'center',
                    p: 2.5,
                    background: `linear-gradient(135deg, ${alpha(stat.color, 0.08)} 0%, rgba(0,0,0,0.2) 100%)`,
                    borderRadius: 3,
                    border: `1px solid ${alpha(stat.color, 0.15)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      border: `1px solid ${alpha(stat.color, 0.3)}`,
                      boxShadow: `0 8px 25px ${alpha(stat.color, 0.1)}`,
                    },
                  }}>
                    <Box sx={{ color: stat.color, mb: 1 }}>{stat.icon}</Box>
                    <Typography variant="h5" sx={{ color: stat.color, fontWeight: 800, lineHeight: 1.1 }}>
                      {stat.value || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#8AA87D', display: 'block', mt: 0.5, fontWeight: 500 }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ReportStats;
